import { Routes, Route, Navigate, useSearchParams } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { MyContextProvider } from './context';
import Layout from './layout';
import Error from './views/error';
import Login from './views/auth/login';
import AcceptInvite from './views/auth/acceptInvite';
import Register from './views/auth/register';
import VerifyEmail from './views/auth/verifyEmail';
import ForgotPassword from './views/auth/forgotPassword';
import ResetPassword from './views/auth/resetPassword';
import IframePreview from './views/settings/iframePreview';
import StoreIframe from './views/settings/iframe';
import ProtectedRoute from './components/ProtectedRoute';
import RoleRoute from './components/RoleRoute';
import ApproverRoute from './components/ApproverRoute';
import { ADMIN_ROLES, USER_ROLES, PLATFORMADMIN_ROLES } from './components/navConfig';
import StoreGeneral from './views/settings/general';
import StoreUsersList from './views/settings/users';
import StoreBranding from './views/settings/branding';
import StoreEmail from './views/settings/email';
import StorePaymentMethods from './views/settings/paymentMethods';
import StoreShippingMethods from './views/settings/shippingMethods';
import StoreFaq from './views/settings/faq';
import StoreErp from './views/settings/erp';
import StoreApiTokens from './views/settings/apiTokens';
import StoreWebhooks from './views/settings/webhooks';
import StoreSetup from './views/settings/setup';
import Holidays from './views/settings/holidays';
import OrdersList from './views/orders';
import OrderDetail from './views/orders/detail';
import FulfillmentsList from './views/fulfillments';
import FulfillmentDetail from './views/fulfillments/detail';
import ProductsList from './views/products';
import ProductEditor from './views/products/editor';
import KitsList from './views/products/kits';
import CategoriesList from './views/products/categories';
import CouponsList from './views/coupons';
import CouponEditor from './views/coupons/editor';
import FulfillmentRoutingList from './views/products/fulfillmentRouting';
import ProductErpMappings from './views/products/erpMappings';
import InventoryLevelsList from './views/inventory';
import InventoryChangeLog from './views/inventory/changeLog';
import CustomersList from './views/customers';
import CustomerDetail from './views/customers/detail';
import StoreAccountsList from './views/storeAccounts';
import StoreAccountDetail from './views/storeAccounts/detail';
import OrdersReport from './views/reports/orders';
import FulfillmentsReport from './views/reports/fulfillments';
import InventoryReport from './views/reports/inventory';
import LoyaltyReport from './views/reports/loyalty';
import CouponUsageReport from './views/reports/couponUsage';
import SyncHealthReport from './views/reports/syncHealth';
import WebhookDeliveriesReport from './views/reports/webhookDeliveries';
import CustomFulfillmentReport from './views/reports/customFulfillment';
import WorkflowsList from './views/workflows';
import WorkflowEditor from './views/workflows/editor';
import WorkflowRunsList from './views/workflows/runs';
import WorkflowRunDetail from './views/workflows/runs/detail';
import WorkflowApprovalsList from './views/workflows/approvals';
import WorkflowExceptionsList from './views/workflows/exceptions';
import UsersList from './views/admin/users';
import PaceAccountSettings from './views/admin/paceAccount';
import AuditLog from './views/admin/auditLog';
import EventLog from './views/admin/eventLog';
import Home from './views/home';
import CommandCenter from './views/commandCenter';

// Old /approvals links (emails already in inboxes, Command Center tile)
// land on the orders list's needs-approval view, keeping the highlighted order.
function ApprovalsRedirect() {
	const [searchParams] = useSearchParams();
	const orderId = searchParams.get('orderId');
	const query = new URLSearchParams({ view: 'approval', ...(orderId ? { orderId } : {}) });
	return <Navigate to={`/orders?${query.toString()}`} replace />;
}

const App = () => {
	return (
		<MyContextProvider>
			<Routes>
				<Route path='/login' element={<Login />} />
				<Route path='/invite/accept' element={<AcceptInvite />} />
				<Route path='/register' element={<Register />} />
				<Route path='/verify-email' element={<VerifyEmail />} />
				<Route path='/forgot-password' element={<ForgotPassword />} />
				<Route path='/reset-password' element={<ResetPassword />} />
				{/* Full-screen (no admin Layout): stands in for an iframe store's host app. */}
				<Route
					path='/iframe-preview'
					element={
						<ProtectedRoute>
							<IframePreview />
						</ProtectedRoute>
					}
				/>
				<Route
					path='/'
					element={
						<ProtectedRoute>
							<Layout />
						</ProtectedRoute>
					}
				>
					<Route index element={<Home />} />
					<Route path='command-center' element={<CommandCenter />} />

					<Route path='orders' element={<OrdersList />} />
					<Route path='orders/:id' element={<OrderDetail />} />

					{/* The Approvals page now lives on /orders as the "Needs approval"
					filter. Kept as a redirect because pi_ecommerce_api still links here:
					the approval-request email (`${adminAppUrl}/approvals?orderId=<id>`,
					email.service.ts), the public decision page, and the Command Center
					Pending Approvals tile. */}
					<Route path='approvals' element={<ApprovalsRedirect />} />

					<Route path='fulfillments' element={<FulfillmentsList />} />
					<Route path='fulfillments/:id' element={<FulfillmentDetail />} />

					<Route
						path='workflows'
						element={
							<RoleRoute roles={ADMIN_ROLES}>
								<WorkflowsList />
							</RoleRoute>
						}
					/>
					<Route
						path='workflows/new'
						element={
							<RoleRoute roles={ADMIN_ROLES}>
								<WorkflowEditor />
							</RoleRoute>
						}
					/>
					<Route
						path='workflows/runs'
						element={
							<RoleRoute roles={ADMIN_ROLES}>
								<WorkflowRunsList />
							</RoleRoute>
						}
					/>
					<Route
						path='workflows/runs/:id'
						element={
							<RoleRoute roles={ADMIN_ROLES}>
								<WorkflowRunDetail />
							</RoleRoute>
						}
					/>
					<Route
						path='workflows/approvals'
						element={
							<ApproverRoute>
								<WorkflowApprovalsList />
							</ApproverRoute>
						}
					/>
					<Route
						path='workflows/exceptions'
						element={
							<RoleRoute roles={ADMIN_ROLES}>
								<WorkflowExceptionsList />
							</RoleRoute>
						}
					/>
					<Route
						path='workflows/:id'
						element={
							<RoleRoute roles={ADMIN_ROLES}>
								<WorkflowEditor />
							</RoleRoute>
						}
					/>

					<Route path='products' element={<ProductsList />} />
					<Route path='products/new' element={<ProductEditor />} />
					<Route path='products/kits' element={<KitsList />} />
					<Route path='products/kits/new' element={<ProductEditor kitMode />} />
					<Route path='products/kits/:id/edit' element={<ProductEditor kitMode />} />
					<Route
						path='products/categories'
						element={
							<RoleRoute roles={USER_ROLES}>
								<CategoriesList />
							</RoleRoute>
						}
					/>
					<Route
						path='products/fulfillment-routing'
						element={
							<RoleRoute roles={ADMIN_ROLES}>
								<FulfillmentRoutingList />
							</RoleRoute>
						}
					/>
					<Route
						path='products/erp-mappings'
						element={
							<RoleRoute roles={ADMIN_ROLES}>
								<ProductErpMappings />
							</RoleRoute>
						}
					/>
					<Route path='products/:id/edit' element={<ProductEditor />} />

					<Route
						path='coupons'
						element={
							<RoleRoute roles={ADMIN_ROLES}>
								<CouponsList />
							</RoleRoute>
						}
					/>
					<Route
						path='coupons/new'
						element={
							<RoleRoute roles={ADMIN_ROLES}>
								<CouponEditor />
							</RoleRoute>
						}
					/>
					<Route
						path='coupons/:id/edit'
						element={
							<RoleRoute roles={ADMIN_ROLES}>
								<CouponEditor />
							</RoleRoute>
						}
					/>

					<Route
						path='inventory'
						element={
							<RoleRoute roles={USER_ROLES}>
								<InventoryLevelsList />
							</RoleRoute>
						}
					/>
					<Route
						path='inventory/change-log'
						element={
							<RoleRoute roles={ADMIN_ROLES}>
								<InventoryChangeLog />
							</RoleRoute>
						}
					/>

					<Route path='customers' element={<CustomersList />} />
					<Route path='customers/:id' element={<CustomerDetail />} />

					<Route path='store-accounts' element={<StoreAccountsList />} />
					<Route path='store-accounts/:id' element={<StoreAccountDetail />} />

					<Route path='reports/orders' element={<OrdersReport />} />
					<Route path='reports/fulfillments' element={<FulfillmentsReport />} />
					<Route
						path='reports/inventory'
						element={
							<RoleRoute roles={ADMIN_ROLES}>
								<InventoryReport />
							</RoleRoute>
						}
					/>
					<Route
						path='reports/loyalty'
						element={
							<RoleRoute roles={ADMIN_ROLES}>
								<LoyaltyReport />
							</RoleRoute>
						}
					/>
					<Route
						path='reports/coupon-usage'
						element={
							<RoleRoute roles={ADMIN_ROLES}>
								<CouponUsageReport />
							</RoleRoute>
						}
					/>
					<Route
						path='reports/sync-health'
						element={
							<RoleRoute roles={ADMIN_ROLES}>
								<SyncHealthReport />
							</RoleRoute>
						}
					/>
					<Route
						path='reports/webhook-deliveries'
						element={
							<RoleRoute roles={ADMIN_ROLES}>
								<WebhookDeliveriesReport />
							</RoleRoute>
						}
					/>
					<Route
						path='reports/custom-fulfillment'
						element={
							<RoleRoute roles={ADMIN_ROLES}>
								<CustomFulfillmentReport />
							</RoleRoute>
						}
					/>

					<Route
						path='settings'
						element={
							<RoleRoute roles={ADMIN_ROLES}>
								<StoreGeneral />
							</RoleRoute>
						}
					/>
					<Route
						path='settings/users'
						element={
							<RoleRoute roles={ADMIN_ROLES}>
								<StoreUsersList />
							</RoleRoute>
						}
					/>
					<Route
						path='settings/branding'
						element={
							<RoleRoute roles={ADMIN_ROLES}>
								<StoreBranding />
							</RoleRoute>
						}
					/>
					<Route
						path='settings/email'
						element={
							<RoleRoute roles={ADMIN_ROLES}>
								<StoreEmail />
							</RoleRoute>
						}
					/>
					{/* Consolidated into the Email tab strip above - kept so old links/bookmarks still resolve. */}
					<Route path='settings/email-templates' element={<Navigate to='/settings/email' replace />} />
					<Route path='settings/notifications' element={<Navigate to='/settings/email' replace />} />
					<Route
						path='settings/payment-methods'
						element={
							<RoleRoute roles={ADMIN_ROLES}>
								<StorePaymentMethods />
							</RoleRoute>
						}
					/>
					<Route
						path='settings/shipping-methods'
						element={
							<RoleRoute roles={ADMIN_ROLES}>
								<StoreShippingMethods />
							</RoleRoute>
						}
					/>
					<Route
						path='settings/faq'
						element={
							<RoleRoute roles={ADMIN_ROLES}>
								<StoreFaq />
							</RoleRoute>
						}
					/>
					<Route
						path='settings/holidays'
						element={
							<RoleRoute roles={PLATFORMADMIN_ROLES}>
								<Holidays />
							</RoleRoute>
						}
					/>
					<Route
						path='settings/erp'
						element={
							<RoleRoute roles={PLATFORMADMIN_ROLES}>
								<StoreErp />
							</RoleRoute>
						}
					/>
					<Route
						path='settings/tokens'
						element={
							<RoleRoute roles={ADMIN_ROLES}>
								<StoreApiTokens />
							</RoleRoute>
						}
					/>
					<Route
						path='settings/iframe'
						element={
							<RoleRoute roles={PLATFORMADMIN_ROLES}>
								<StoreIframe />
							</RoleRoute>
						}
					/>
					<Route
						path='settings/webhooks'
						element={
							<RoleRoute roles={ADMIN_ROLES}>
								<StoreWebhooks />
							</RoleRoute>
						}
					/>
					{/* Not role-gated: layout.js auto-redirects any authenticated user
					here from "/" when the active store's setup is incomplete. */}
					<Route path='settings/setup' element={<StoreSetup />} />

					<Route
						path='admin/users'
						element={
							<RoleRoute roles={PLATFORMADMIN_ROLES}>
								<UsersList />
							</RoleRoute>
						}
					/>
					<Route
						path='admin/pace-account'
						element={
							<RoleRoute roles={ADMIN_ROLES}>
								<PaceAccountSettings />
							</RoleRoute>
						}
					/>
					<Route
						path='admin/audit-log'
						element={
							<RoleRoute roles={ADMIN_ROLES}>
								<AuditLog />
							</RoleRoute>
						}
					/>
					<Route
						path='admin/event-log'
						element={
							<RoleRoute roles={ADMIN_ROLES}>
								<EventLog />
							</RoleRoute>
						}
					/>

					<Route path='error' element={<Error />} />
				</Route>
			</Routes>
			<ToastContainer position='top-right' autoClose={3000} hideProgressBar closeOnClick pauseOnHover />
		</MyContextProvider>
	);
};

export default App;
